import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A horizontal stacked bar chart uses horizontally aligned bars divided into segments to
          represent subcategories. The total length of the bar reflects the overall value, while the
          segments illustrate the proportion or contribution of each subcategory. This chart type is
          ideal for comparing cumulative values and part-to-whole relationships while accommodating
          long category labels for better readability.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">Horizontal stacked bar charts are best suited for:</p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Displaying Part-to-Whole Relationships:</strong> They effectively show how
            different components contribute to a total across categories, such as the breakdown of
            sales by product type across various regions.
          </li>
          <li>
            <strong>Comparing Compositions Across Categories:</strong> They allow for easy
            comparison of the distribution of subcategories across different groups or categories,
            such as the share of each department&apos;s budget across multiple years.
          </li>
          <li>
            <strong>Visualizing Cumulative Data:</strong> Horizontal stacked bar charts are ideal
            for tracking cumulative data across categories, like the progression of total yearly
            expenses, with each segment representing a specific cost type.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          Horizontal stacked bar charts are useful for visualizing the composition of data across
          categories, but they have some limitations that should be kept in mind:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Difficulty in Comparing Subcategories Across Bars:</strong> When subcategories
            are stacked on top of one another, it becomes challenging to compare individual parts
            across different bars, especially if the differences are subtle.
          </li>
          <li>
            <strong>Clutter with Many Subcategories:</strong> If there are numerous subcategories,
            the chart can become overcrowded, reducing readability and making it harder to identify
            key trends or patterns.
          </li>
          <li>
            <strong>Limited Precision for Small Differences:</strong> When there are small
            differences between the subcategories, it can be difficult to accurately represent them,
            which may lead to misinterpretation of the data.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          Color selection is crucial for horizontal stacked bar charts to ensure clarity and
          highlight key insights. Distinct, contrasting colors can help differentiate between
          various categories and subcategories, making it easier to understand the chart at a
          glance. Using a consistent color scheme across the chart improves visual coherence and
          comparison. Gradual color transitions can represent intensity or progression, while
          neutral tones can be used to emphasize the most critical data points without distracting
          from the overall message. Thoughtful color choices enhance the chart&apos;s readability
          and prevent visual clutter.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['qualitative', 'sequential', 'diverging']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          Horizontal stacked bar charts are effective for showing data composition, but they come
          with several challenges:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Difficulty in Comparing Subcategories:</strong> The stacked format can make it
            challenging to compare the size of subcategories across different bars, especially when
            the differences are subtle.
          </li>
          <li>
            <strong>Limited Space for Labels:</strong> As the bars expand horizontally, there may
            not be enough space to fit labels or text, particularly for larger datasets, which can
            lead to readability issues.
          </li>
          <li>
            <strong>Cluttered Display with Multiple Categories:</strong> With a large number of
            categories or subcategories, the chart can become overcrowded, making it difficult for
            viewers to focus on important trends or patterns.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
